/**
 * Events open for public registration (inscripcion.addbolivia.org/ or /inscripcion).
 * Several events can run at the same time in different cities, so this lists
 * them with location and dates. With exactly one open event it goes straight
 * to its form.
 */

import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, ChevronRight, Loader2, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { parseLocalDate } from '@/models/event';
import { getEventFlyerUrl } from '@/Services/events.service';
import { listOpenEvents, type OpenEventSummary } from '../publicRegistrationRepository';
import { PublicShell } from '../components/PublicShell';
import { registrationPath } from '../publicUrls';

function formatDates(e: OpenEventSummary) {
  const fmt = (v: string) =>
    parseLocalDate(v)?.toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' }) ?? v;
  return e.start_date === e.end_date ? fmt(e.start_date) : `${fmt(e.start_date)} – ${fmt(e.end_date)}`;
}

export function OpenEventsPage() {
  const { data: events, isLoading, error } = useQuery({
    queryKey: ['public_open_events'],
    queryFn: listOpenEvents,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (events && events.length === 1) {
    return <Navigate to={registrationPath(events[0].code)} replace />;
  }

  return (
    <PublicShell>
      {error || !events || events.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertTriangle className="size-10 text-muted-foreground" />
          <p className="text-muted-foreground">
            {error ? 'No se pudo cargar la lista de eventos.' : 'No hay eventos con inscripción abierta en este momento.'}
          </p>
        </div>
      ) : (
        <>
          <h1 className="text-center text-lg font-semibold">Elija el evento al que desea inscribirse</h1>
          <div className="space-y-3">
            {events.map((e) => {
              const flyer = getEventFlyerUrl(e.flyer_filename);
              return (
                <Link key={e.code} to={registrationPath(e.code)} className="block">
                  <Card className="transition-colors hover:bg-muted/50">
                    <CardContent className="flex items-center gap-4 p-4">
                      {flyer && <img src={flyer} alt="" className="size-16 shrink-0 rounded object-cover" />}
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="font-semibold leading-tight">{e.name}</p>
                        {e.location && (
                          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <MapPin className="size-4 shrink-0" /> {e.location}
                          </p>
                        )}
                        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <CalendarDays className="size-4 shrink-0" /> {formatDates(e)}
                        </p>
                        <p className="font-mono text-xs text-muted-foreground">Código: {e.code}</p>
                      </div>
                      <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </PublicShell>
  );
}
